import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Star, Users, Calendar, MapPin, ShoppingBag } from 'lucide-react';
import '../../styles/marketplace.css';
import { marketplaceAPI } from '../../services/marketplaceAPI';
import ProductCard from './ProductCard';

const ProviderStoreModal = ({ providerId, isOpen, onClose, allProducts, onAddToCart, selectedCurrency, rates, onViewDetails }) => {
    if (!isOpen || !providerId) return null;

    const [provider, setProvider] = useState(null);
    const [loading, setLoading] = useState(true);
    const [providerProducts, setProviderProducts] = useState([]);

    useEffect(() => {
        if (isOpen && providerId) {
            setLoading(true);

            // Fetch Details
            marketplaceAPI.getProviderDetails(providerId)
                .then(res => setProvider(res.data))
                .catch(err => console.error(err));

            // Filter Products in memory (since we have allProducts prop usually, or we can fetch)
            // Assuming allProducts is passed appropriately, or we can fetch from API if needed.
            // For now, let's filter the generic list to avoid prop drilling mania if generic list is available.
            // Actually, better to fetch new list or use prop to reuse existing state from parent.
            if (allProducts) {
                setProviderProducts(allProducts.filter(p => p.providerId === providerId));
            } else {
                marketplaceAPI.getProducts().then(res => {
                    setProviderProducts(res.data.filter(p => p.providerId === providerId));
                });
            }

            setLoading(false);
        }
    }, [isOpen, providerId, allProducts]);

    if (loading && !provider) return null; // Or loader

    return createPortal(
        <div className="mp-modal-overlay" style={{ zIndex: 10000 }}>
            <div className="mp-modal-content" style={{ maxWidth: '1000px', width: '95%', height: '90vh', display: 'flex', flexDirection: 'column', padding: 0 }}>

                {/* Banner Header */}
                <div style={{
                    height: '200px',
                    background: provider?.banner ? `url(${provider.banner}) center/cover` : '#8548A8',
                    position: 'relative'
                }}>
                    <button
                        onClick={onClose}
                        style={{ position: 'absolute', top: 16, right: 16, background: 'white', border: 'none', borderRadius: '50%', padding: 8, cursor: 'pointer' }}
                    >
                        <X size={24} />
                    </button>

                    <div style={{
                        position: 'absolute',
                        bottom: '-40px',
                        left: '40px',
                        display: 'flex',
                        alignItems: 'end',
                        gap: '20px'
                    }}>
                        <img
                            src={provider?.image}
                            alt={provider?.name}
                            style={{ width: '100px', height: '100px', borderRadius: '50%', border: '4px solid white', background: 'white', objectFit: 'cover' }}
                        />
                        <div style={{ marginBottom: '40px' }}> {/* Push text up into banner area slightly or keep usually */}
                            {/* Actually standard profile layout */}
                        </div>
                    </div>
                </div>

                {/* Profile Info Bar */}
                <div style={{ paddingTop: '50px', paddingLeft: '40px', paddingRight: '40px', paddingBottom: '24px', borderBottom: '1px solid #E5E7EB' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
                        <div>
                            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '800' }}>{provider?.name}</h1>
                            <p style={{ color: '#6B7280', marginTop: '8px', maxWidth: '600px' }}>{provider?.description}</p>

                            <div style={{ display: 'flex', gap: '24px', marginTop: '16px', color: '#4B5563', fontSize: '14px' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Star size={18} fill="#FBBF24" color="#FBBF24" />
                                    Looking good ({provider?.rating})
                                </span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Users size={18} />
                                    {provider?.followers} Followers
                                </span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Calendar size={18} />
                                    Joined {provider?.joined}
                                </span>
                            </div>
                        </div>

                        <div>
                            <button className="mp-btn-primary" style={{ padding: '10px 24px' }}>Follow Store</button>
                        </div>
                    </div>
                </div>

                {/* Products Grid */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '40px', background: '#F9FAFB' }}>
                    <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ShoppingBag />
                        Store Listings ({providerProducts.length})
                    </h3>

                    {providerProducts.length > 0 ? (
                        <div className="mp-product-grid">
                            {providerProducts.map(product => (
                                <ProductCard
                                    key={product.id}
                                    product={product}
                                    onAddToCart={onAddToCart}
                                    onViewDetails={onViewDetails} // Allow viewing details inside the store modal? Yes 
                                    selectedCurrency={selectedCurrency}
                                    rates={rates}
                                // Important: Prevent recursive opening of store? 
                                // Or just pass empty handler for onOpenStore if we don't want nesting
                                />
                            ))}
                        </div>
                    ) : (
                        <p>No products found for this seller.</p>
                    )}
                </div>

            </div>
        </div>,
        document.body
    );
};

export default ProviderStoreModal;
